// src/App.js
import { useMemo, useState } from "react";
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from "xlsx";
import "./theme.css"; // keep the theme

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// Flatten useful Jira fields into a flat row for tables/Excel
function flattenIssue(issue) {
  const f = issue?.fields ?? {};
  const get = (obj, path, fallback = "") =>
    path.split(".").reduce((o, k) => (o && o[k] != null ? o[k] : undefined), obj) ?? fallback;

  return {
    key: issue.key,
    summary: f.summary ?? "",
    status: get(f, "status.name", ""),
    assignee: get(f, "assignee.displayName", "Unassigned"),
    reporter: get(f, "reporter.displayName", ""),
    priority: get(f, "priority.name", ""),
    issueType: get(f, "issuetype.name", ""),
    created: f.created ? formatDate(f.created) : "",
    updated: f.updated ? formatDate(f.updated) : "",
    duedate: f.duedate ? formatDate(f.duedate) : "",
    labels: Array.isArray(f.labels) ? f.labels.join(", ") : "",
    components: Array.isArray(f.components) ? f.components.map((c) => c.name).join(", ") : "",
    fixVersions: Array.isArray(f.fixVersions) ? f.fixVersions.map((v) => v.name).join(", ") : "",
    organization: (() => {
      const org = f["Organizations"] || f["organizations"] || f["customfield_organizations"];
      if (!org) return "";
      if (Array.isArray(org)) return org.map((o) => o.name || o).join(", ");
      return org.name || String(org);
    })(),
    _raw: f, // internal only
  };
}

// Build dynamic columns from the flattened rows (limit to readable scalar keys)
function computeColumns(rows, maxCols = 20) {
  const allKeys = new Set();
  rows.forEach((r) =>
    Object.keys(r).forEach((k) => {
      if (k === "_raw") return;
      const v = r[k];
      if (v == null) return;
      if (typeof v === "object") return;
      allKeys.add(k);
    })
  );
  const preferred = [
    "key",
    "summary",
    "status",
    "assignee",
    "reporter",
    "priority",
    "issueType",
    "created",
    "updated",
    "duedate",
    "labels",
    "components",
    "fixVersions",
    "organization",
  ];
  const order = [
    ...preferred.filter((k) => allKeys.has(k)),
    ...[...allKeys].filter((k) => !preferred.includes(k)),
  ];
  return order.slice(0, maxCols);
}

export default function App() {
  // Inputs
  const [projectKey, setProjectKey] = useState("");
  const [orgName, setOrgName] = useState("");
  const [maxResults, setMaxResults] = useState(20);
  const [jqlOverride, setJqlOverride] = useState("");

  // UI/data
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState([]);
  const [error, setError] = useState("");
  const [wantMaxFields, setWantMaxFields] = useState(true);
  const [flatRows, setFlatRows] = useState([]);
  const [tableCols, setTableCols] = useState([]);

  const total = useMemo(() => issues?.length ?? 0, [issues]);

  function exportToExcel() {
    if (!flatRows.length) return;
    const rows = flatRows.map(({ _raw, ...rest }) => rest);
    const ws = XLSXUtils.json_to_sheet(rows);
    const wb = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(wb, ws, "Issues");
    const nameBits = [
      projectKey ? `proj-${projectKey}` : null,
      orgName ? `org-${orgName}` : null,
      new Date().toISOString().slice(0, 10),
    ]
      .filter(Boolean)
      .join("_") || "jira";
    XLSXWriteFile(wb, `${nameBits}_issues.xlsx`);
  }

  async function fetchIssues(e) {
    e?.preventDefault();
    setLoading(true);
    setError("");
    setIssues([]);
    setFlatRows([]);
    setTableCols([]);

    try {
      const res = await fetch("/api/jira", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jql: jqlOverride.trim() || undefined,
          projectKey: projectKey.trim(),
          orgName: orgName.trim(),
          maxResults: Math.min(Number(maxResults || 20), 100),
          // ask server for broader data if checked
          ...(wantMaxFields ? { fields: ["*navigable"] } : undefined),
        }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text };
      }

      if (!res.ok) {
        const msg =
          data?.error ||
          (Array.isArray(data?.errorMessages) && data.errorMessages.join(", ")) ||
          `HTTP ${res.status}`;
        throw new Error(msg);
      }

      const items = data.issues || [];
      setIssues(items);

      const rows = items.map(flattenIssue);
      setFlatRows(rows);
      setTableCols(computeColumns(rows));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container">
      {/* Header */}
      <div className="pill">
        <span className="dot" />
        <span>Live • JSM Integration Suite</span>
      </div>
      <h1 className="header-title">JSM Ticket Queue</h1>
      <p className="header-sub">
        Frontend UI that talks to a lightweight backend function to securely fetch data from Atlassian.
        Enter a <strong>Project Key</strong> and optionally an <strong>Organization</strong>. You can
        override with custom JQL anytime.
      </p>

      {/* BIG NOTE — server agnostic */}
      <div className="card" style={{ borderColor: "#3b82f6" }}>
        <div className="card-body">
          <strong>BIG NOTE:</strong> The backend “function” is <em>server-agnostic</em>. You can run it on
          Netlify Functions, Vercel Functions, AWS Lambda, Cloudflare Workers, your own Node server,
          or even a Docker/PM2 process on any active internet-connected PC. The UI just needs an HTTPS
          endpoint to call.
        </div>
      </div>

      {/* About Project */}
      <div className="card">
        <div className="card-body grid">
          <div>
            <h2 style={{ margin: 0 }}>About this project</h2>
            <p className="small" style={{ marginTop: 6 }}>
              This solution is intentionally simple and robust: a focused UI and a small backend function that
              bridges to Atlassian using your API key.
            </p>
          </div>

          <div className="grid grid-auto">
            <div className="card">
              <div className="card-body">
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Part 1 — Frontend (UI)</h3>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Clean, guided form to build a query (Project Key + Organization or custom JQL).</li>
                  <li>Calls your backend function over HTTPS; no secrets live in the browser.</li>
                  <li>Shows results in a fast, sticky-header table with Excel export.</li>
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Part 2 — Backend Function</h3>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Always ready to receive requests from the UI.</li>
                  <li>On demand, it reads the request, uses your configured API key/project to fetch from Atlassian, then returns clean JSON.</li>
                  <li>Lives as a serverless function or a small Node service—your choice.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-auto">
            <div className="card">
              <div className="card-body">
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Pros & Highlights</h3>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  <li><strong>Security-first</strong>: API key stays on the server side.</li>
                  <li><strong>Zero vendor lock-in</strong>: works on Netlify, Vercel, AWS, your PC—anywhere.</li>
                  <li><strong>Fast iteration</strong>: simple UI, instant deploys.</li>
                  <li><strong>Operational clarity</strong>: table is flat, exportable, and easy to filter.</li>
                  <li><strong>Minimal surface area</strong>: fewer moving parts, fewer outages.</li>
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Appreciations & Use Cases</h3>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Support teams: quick queue visibility for a project/org.</li>
                  <li>Leads/Managers: export to Excel for reporting without Jira admin overhead.</li>
                  <li>Ops/Automation: can be embedded behind SSO or run in an internal network.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3 style={{ marginTop: 0, fontSize: 16 }}>Requirements</h3>
              <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                <li><strong>Only</strong> one item: a valid Atlassian API key (and base URL / project config on the server).</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Form Card (core flow unchanged; keep details higher level here, put function deep details below) */}
      <form onSubmit={fetchIssues} className="card">
        <div className="card-body grid">
          <div className="grid grid-auto">
            <label className="label">
              <span>Project Key</span>
              <input
                className="input"
                value={projectKey}
                onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
                placeholder="SUP"
              />
            </label>

            <label className="label">
              <span>Organization</span>
              <input
                className="input"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="one"
              />
              <small className="small">
                Server will use <code>cf[ORG_FIELD_ID]</code> for this.
              </small>
            </label>

            <label className="label">
              <span>Max Results (≤ 100)</span>
              <input
                className="input"
                type="number"
                min="1"
                max="100"
                value={maxResults}
                onChange={(e) => setMaxResults(e.target.value)}
              />
            </label>
          </div>

          <label className="label">
            <span>JQL (optional override)</span>
            <input
              className="input"
              value={jqlOverride}
              onChange={(e) => setJqlOverride(e.target.value)}
              placeholder='(leave blank to auto-build) e.g. project = SUP AND cf[10002] = "one" ORDER BY created DESC'
            />
          </label>

          <div className="row" style={{ marginTop: 2 }}>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? "Fetching..." : "Fetch Issues"}
            </button>

            <label className="row small" style={{ gap: 8 }}>
              <input
                className="checkbox"
                type="checkbox"
                checked={wantMaxFields}
                onChange={(e) => setWantMaxFields(e.target.checked)}
              />
              Request max fields (*navigable)
            </label>

            <button
              type="button"
              onClick={exportToExcel}
              disabled={!flatRows.length}
              className="btn btn-ghost"
              title={flatRows.length ? "Export current results to Excel" : "Run a fetch first"}
            >
              Export to Excel
            </button>

            {error && <span className="badge err">Error: {error}</span>}
            {!error && total > 0 && (
              <span className="badge ok">
                <strong>{total}</strong> issue{total === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
      </form>

      {/* Results */}
      {flatRows.length > 0 ? (
        <>
          <div className="card">
            <div className="card-body" style={{ paddingBottom: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Issues</h2>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {tableCols.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {flatRows.map((row, idx) => (
                    <tr key={idx}>
                      {tableCols.map((col) => (
                        <td key={col}>{row[col] ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        !loading && !error && (
          <div className="card">
            <div className="card-body small">No issues to display yet.</div>
          </div>
        )
      )}

      {/* Function details (kept lower on the page as requested) */}
      <div className="card">
        <div className="card-body">
          <h2 style={{ marginTop: 0, fontSize: 18 }}>How it works (deeper)</h2>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            <li><strong>Frontend</strong> captures input (Project Key, Organization, or custom JQL).</li>
            <li>On “Fetch Issues”, the UI sends a POST to your <em>backend function</em> with the query payload.</li>
            <li>The <strong>function</strong> receives the payload, injects your Atlassian <strong>API key</strong> from secure server-side config, and calls Atlassian APIs.</li>
            <li>The function returns normalized JSON; the UI flattens and renders it in a table and enables Excel export.</li>
            <li>Only the UI is public; your secret stays server-side.</li>
          </ol>
        </div>
      </div>

      <div className="hr" />
      <p className="tip">
        Tip: Leave JQL empty and just use Project + Organization. Configure <code>ORG_FIELD_ID</code> on the server to
        map Organization correctly.
      </p>
    </div>
  );
}
